import { useCallback, useEffect, useRef } from "react";
import { api } from "./api";

export function useQueuedProjectLoad(loadOnce) {
    const loadOnceRef = useRef(loadOnce);
    const inFlightRef = useRef(null);
    const queuedBranchRef = useRef(null);
    loadOnceRef.current = loadOnce;

    return useCallback((branchId = "main") => {
        queuedBranchRef.current = branchId || "main";
        if (inFlightRef.current)
            return inFlightRef.current;
        const drain = async () => {
            while (queuedBranchRef.current) {
                const nextBranchId = queuedBranchRef.current;
                queuedBranchRef.current = null;
                await loadOnceRef.current(nextBranchId);
            }
        };
        const pending = drain().finally(() => {
            inFlightRef.current = null;
        });
        inFlightRef.current = pending;
        return pending;
    }, []);
}

export function useDeferredRetry() {
    const timerRef = useRef(null);

    const clearRetry = useCallback(() => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const scheduleRetry = useCallback((callback, delayMs = 2500) => {
        if (timerRef.current !== null)
            return false;
        timerRef.current = window.setTimeout(() => {
            timerRef.current = null;
            callback();
        }, delayMs);
        return true;
    }, []);

    useEffect(() => clearRetry, [clearRetry]);
    return { timerRef, clearRetry, scheduleRetry };
}

export function useGuttenbergJobPolling({
    projectId,
    job,
    onUpdate,
    onTerminal,
    onTransientError,
    intervalMs = 2500,
}) {
    const callbacksRef = useRef({ onUpdate, onTerminal, onTransientError });
    callbacksRef.current = { onUpdate, onTerminal, onTransientError };

    useEffect(() => {
        if (!job || !["queued", "running"].includes(job.status))
            return undefined;
        let cancelled = false;
        let timer = null;

        const poll = async () => {
            try {
                const updated = await api.job(projectId, job.id);
                if (cancelled)
                    return;
                callbacksRef.current.onUpdate?.(updated);
                if (["completed", "failed", "cancelled"].includes(updated.status)) {
                    await callbacksRef.current.onTerminal?.(updated);
                    return;
                }
            }
            catch (error) {
                if (!cancelled)
                    callbacksRef.current.onTransientError?.(error);
            }
            if (!cancelled)
                timer = window.setTimeout(poll, intervalMs);
        };

        timer = window.setTimeout(poll, intervalMs);
        return () => {
            cancelled = true;
            if (timer !== null)
                window.clearTimeout(timer);
        };
    }, [projectId, job?.id, job?.status, intervalMs]);
}
